import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { isNull, isUndefined } from 'lodash/fp';
import classnames from 'classnames';

import styles from '../Clients.module.scss';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Download, Ellipsis } from '../../../shared/icons';
import { IconButton } from '../../../shared/button/IconButton';
import { ClientTermsAndConditionsDocument, downloadDocumentStarted, getFilesToUpload, getIsDownloading, getIsUploading, setUploadTermsAndConditionsDocuments } from '../store';
import { Position } from '../../../shared/modal/PositionModal';
import { ActionModal } from '../../../shared/modal/ActionModal';
import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { dateNow, formatDate } from '../../../../utils/luxon';
import { MimeType } from '../../../constants/MimeType';

const { lightestGrey, white } = styles;

interface ClientTermsAndConditionsProps {
    clientId?: number;
    clientTermsAndConditionsDocuments: ClientTermsAndConditionsDocument[];
}

interface DocumentTableData {
    id: number | null;
    description: string;
    createdDate: string;
}

export const ClientTermsAndConditions: React.FC<ClientTermsAndConditionsProps> = ({ clientTermsAndConditionsDocuments, clientId }) => {
    const [termsAndConditionsModalPosition, setTermsAndConditionsModalPosition] = useState<Position | null>(null);
    const [termsAndConditionsModalOpen, setTermsAndConditionsModalOpen] = useState<number | null>(null);

    const dispatch = useAppDispatch();
    const files = useAppSelector(getFilesToUpload);
    const isUploading = useAppSelector(getIsUploading);
    const isDownloading = useAppSelector(getIsDownloading);

    const disabled = useMemo(() => isUndefined(clientId) || isUploading, [clientId, isUploading]);

    const openTermsAndConditionsModal = useCallback((x: number, y: number, id: number | null) => {
        if (!isNull(id)) {
            setTermsAndConditionsModalPosition({ x, y });
            setTermsAndConditionsModalOpen(id);
        }
    }, []);

    const closeTermsAndConditionsModal = useCallback(() => {
        setTermsAndConditionsModalPosition(null);
        setTermsAndConditionsModalOpen(null);
    }, []);

    const download = useCallback((id: number) => !isDownloading && dispatch(downloadDocumentStarted(id)), [dispatch, isDownloading]);

    const termsAndConditionsModalActions = useCallback((id: number) => [
        { label: 'Download', icon: Download, onClick: () => download(id) }
    ], [download]);

    const onDrop = useCallback((acceptedFiles: File[]) => dispatch(setUploadTermsAndConditionsDocuments(acceptedFiles)), [dispatch]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        multiple: true,
        maxFiles: 0,
        accept: {
            [MimeType.PDF]: [],
            [MimeType.DOCX]: [],
            [MimeType.MS_WORD]: [],
            [MimeType.TIFF]: []
        },
        onDrop
    });

    const documentPlural = files && files.length > 1 ? 's' : '';

    const dropzoneText = useMemo(() => {
        if (isDragActive) {
            return 'Drop documents here';
        }
        if (files && files.length) {
            return `Document${documentPlural} successfully added, click here to change the document${documentPlural} to be uploaded`;
        }
        return 'Drag \'n\' drop documents here';
    }, [isDragActive, files, documentPlural]);

    const tableData: DocumentTableData[] = useMemo(() => {
        const documentData = clientTermsAndConditionsDocuments.map(({ clientTermsDocumentId, description, createdDate }) => ({ description, id: clientTermsDocumentId, createdDate: formatDate(createdDate) }));
        const newFilesData = files.map(({ name }) => ({ description: `${name} (PENDING)`, id: null, createdDate: formatDate(dateNow()) }));
        return [...newFilesData, ...documentData];
    }, [clientTermsAndConditionsDocuments, files]);

    const termsAndConditionsHeaders = [{ name: 'Document', width: '60%' }, { name: 'Date Added', width: '35%' }, { name: '', width: '5%' }];

    return (
        <>
            <div className={styles.clientModalContentWrapper}>
                <div className={styles.clientModalHeader}>
                    <div className={styles.clientModalTitle}>Terms & Conditions</div>
                </div>
                <div className={styles.documentUploadWrapper}>
                    <div {...getRootProps({ className: classnames(styles.dropzone, { [styles.dropzoneDisabled]: disabled }) })}>
                        <input {...getInputProps()} />
                        <div className={styles.dropzonePlaceholder}>{dropzoneText}</div>
                    </div>
                </div>
                <div className={styles.termsAndConditionsTable}>
                    <div className={styles.tableHeader}>
                        {termsAndConditionsHeaders.map(({ name, width }) => (
                            <div key={name} className={styles.headerCell} style={{ width }}>{name}</div>
                        ))}
                    </div>
                    <div className={styles.tableWrapper}>
                        <Scrollable>
                            {tableData.map(({ description, createdDate, id }, index) => (
                                <div className={styles.tableRow} key={index} style={{ backgroundColor: index % 2 ? lightestGrey : white }}>
                                    <div className={styles.titleCell} style={{ width: '60%' }}>{description}</div>
                                    <div className={styles.titleCell} style={{ width: '35%' }}>{createdDate}</div>
                                    <div className={styles.toggleCell} style={{ width: '5%' }}>
                                        {!isNull(id) && <IconButton icon={Ellipsis} fontSize={19} onClick={e => openTermsAndConditionsModal(e.clientX, e.clientY, id)} />}
                                    </div>
                                </div>
                            ))}
                        </Scrollable>
                    </div>
                </div>
            </div>
            {termsAndConditionsModalOpen &&
                <ActionModal
                    actions={termsAndConditionsModalActions(termsAndConditionsModalOpen)}
                    closeModal={closeTermsAndConditionsModal}
                    isOpen={!isNull(termsAndConditionsModalPosition)}
                    position={termsAndConditionsModalPosition}
                    testId='client-terms-and-conditions'
                />
            }
        </>
    );
};
